"use client";

import { PictureUpload } from "@/components/common/picture-upload/picture-upload";
import { Fragment, type ReactNode, useEffect, useRef, useState } from "react";
import { getMaxPatronHeight } from "@/service/patron/patron-image";
import { cn } from "@/lib/utils";
import { ClientContentBlocks } from "@/components/common/content-blocks/client-content-blocks";
import {
  ChevronDown,
  ChevronUp,
  ContactRoundIcon,
  NewspaperIcon,
} from "lucide-react";
import { resolvePluralsPublication } from "@/service/text/plurals";
import { ProfileNoImage } from "@/components/common/profile-no-image";

export const AuthorFull = ({ author }: { author: AuthorVM }) => {
  const {
    prefix,
    name,
    type,
    image,
    slug,
    web,
    email,
    facebook,
    twitter,
    instagram,
    content,
    description,
    publicationCount,
  } = author;

  const isCompany = type === "company";
  const noContent = !content || content.length === 0;

  // Add state and refs for collapsible content
  const [isExpanded, setIsExpanded] = useState(false);
  const [shouldCollapse, setShouldCollapse] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  // Check if content height exceeds 200px
  useEffect(() => {
    if (contentRef.current) {
      const contentHeight = contentRef.current.scrollHeight;
      setShouldCollapse(contentHeight > 200);
    }
  }, [content, description]);

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const Wrapper = ({ children }: { children: ReactNode }) => (
    <div className={"@container"}>
      <div
        className={cn(
          "@768:flex @768:items-center mb-20 gap-16",
          isExpanded && "@768:items-start",
        )}
      >
        {children}
      </div>
    </div>
  );

  const contactDataItems = [web, email].filter((_) => _);
  const ContactData = () =>
    contactDataItems.map((item, index) => (
      <Fragment key={item}>
        {item?.includes("@") ? (
          <a
            className={cn(
              `text-base font-medium text-zinc-600`,
              item?.includes("@") ? "text-remedium-blue" : "",
            )}
            href={`mailto:${item}`}
          >
            {item}
          </a>
        ) : (
          <span className={cn(`text-base font-medium text-zinc-600`)}>
            {item}
          </span>
        )}

        <span
          className={"mr-2 text-base font-medium text-zinc-600 last:hidden"}
        >
          ,
        </span>
      </Fragment>
    ));

  const CommonContent = () => (
    <div>
      {isCompany ||
        (prefix && (
          <h5 className={`text-muted-foreground mb-4 text-base font-semibold`}>
            {isCompany ? "Firma" : prefix}
          </h5>
        ))}
      <h1 className={`text-foreground mb-2 text-2xl font-semibold`}>{name}</h1>
      <div
        className={cn(
          `relative max-w-[500px] text-base font-normal text-zinc-600`,
        )}
      >
        <div
          ref={contentRef}
          className={cn(
            "relative overflow-hidden transition-all duration-300",
            !isExpanded && shouldCollapse ? "max-h-[200px]" : "max-h-[600px]",
          )}
        >
          {noContent ? (
            <div>{description}</div>
          ) : (
            <ClientContentBlocks blocks={content} />
          )}

          {!isExpanded && shouldCollapse && (
            <div className="absolute right-0 bottom-0 left-0 h-16 bg-gradient-to-t from-white to-transparent" />
          )}
        </div>

        {shouldCollapse && (
          <button
            onClick={toggleExpand}
            className="text-remedium-blue mt-2 flex items-center gap-1 hover:cursor-pointer hover:underline"
          >
            {isExpanded ? (
              <>
                <span>Schowaj opis</span>
                <ChevronUp size={16} />
              </>
            ) : (
              <>
                <span>Wyświetl cały opis</span>
                <ChevronDown size={16} />
              </>
            )}
          </button>
        )}
      </div>

      <div className={noContent && !description ? "mt-4" : "mt-8"}>
        {contactDataItems.length > 0 && (
          <div className={"mb-3 flex items-center gap-3"}>
            <ContactRoundIcon size={"20px"} />
            <div>
              <ContactData />
            </div>
          </div>
        )}
        <div className={"mb-3 flex items-center gap-3"}>
          <NewspaperIcon size={"20px"} />
          <div className={"text-base font-medium text-zinc-600"}>
            {resolvePluralsPublication(publicationCount)}
          </div>
        </div>
      </div>
    </div>
  );

  const imageClassess = cn(
    ` flex mb-8 mx-auto  h-[328px] w-[328px] min-w-[328px] max-w-[328px]`,
    `@768:h-[400px] @768:mx-0 @768:mb-0 @768:min-h-[400px] @768:max-w-[400px] @768:min-w-[400px]`,
    !image &&
      "@768:h-[200px] @768:mb-0 @768:min-h-[200px] @768:max-w-[200px] @768:min-w-[200px]",
  );

  if (isCompany) {
    return (
      <Wrapper>
        <div
          className={cn(
            "items-center justify-center overflow-hidden rounded-xl bg-zinc-50",
            imageClassess,
          )}
        >
          {image ? (
            <PictureUpload
              image={image}
              maxHeight={getMaxPatronHeight(image, 0.8)}
            />
          ) : (
            <div className={"w-full bg-amber-50"}></div>
          )}
        </div>
        <CommonContent />
      </Wrapper>
    );
  }
  return (
    <Wrapper>
      <div
        className={cn(
          "flex items-center justify-center overflow-hidden rounded-xl",
          imageClassess,
        )}
      >
        {image ? (
          <PictureUpload image={image} />
        ) : (
          <ProfileNoImage
            className={
              "bg-secondary text-muted-foreground h-full w-full rounded-full text-3xl"
            }
            displayName={name}
          />
        )}
      </div>

      <CommonContent />
    </Wrapper>
  );
};
